import React, { useState, useEffect, useCallback } from 'react';
import { Bookmark, BookmarkCheck, Plus, Check, Star, Trash2, Pencil, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub,
  DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  loadSavedViews, saveView, deleteView, renameView, setDefaultView, clearDefaultView,
} from '@/lib/savedCalendarViews';

// Compact Saved Views menu for the calendar toolbar.
// Purely UI — delegates all state mutations to the parent via onApply.
export function SavedViewsMenu({ userId, currentState, activeViewId, onApply, onViewsChanged }) {
  const [store, setStore] = useState({ version: 1, views: [] });
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const refresh = useCallback(() => {
    const next = loadSavedViews(userId);
    setStore(next);
    onViewsChanged?.(next);
  }, [userId, onViewsChanged]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSave = () => {
    const result = saveView(userId, { name: saveName, state: currentState, setAsDefault: saveAsDefault });
    if (!result.ok) { toast.error(result.error || 'Failed to save view'); return; }
    const wasOverwrite = result.view && result.view.createdAt !== result.view.updatedAt;
    toast.success(wasOverwrite ? `View "${result.view.name}" updated` : `View "${result.view.name}" saved`);
    setSaveOpen(false);
    setSaveName('');
    setSaveAsDefault(false);
    refresh();
  };

  const handleApply = (view) => {
    onApply?.(view);
    toast.success(`Loaded "${view.name}"`);
  };

  const handleToggleDefault = (view) => {
    const action = view.isDefault ? clearDefaultView(userId) : setDefaultView(userId, view.id);
    if (!action.ok) { toast.error(action.error || 'Failed to update default'); return; }
    toast.success(view.isDefault ? 'Default cleared' : `"${view.name}" set as default`);
    refresh();
  };

  const handleRename = () => {
    if (!renameTarget) return;
    const result = renameView(userId, renameTarget.id, renameValue);
    if (!result.ok) { toast.error(result.error || 'Rename failed'); return; }
    toast.success('View renamed');
    setRenameTarget(null);
    setRenameValue('');
    refresh();
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    const result = deleteView(userId, deleteTarget.id);
    if (!result.ok) { toast.error('Delete failed'); return; }
    toast.success(`"${deleteTarget.name}" deleted`);
    setDeleteTarget(null);
    refresh();
  };

  const views = store.views;
  const hasViews = views.length > 0;
  const activeView = views.find(v => v.id === activeViewId);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={activeView ? 'default' : 'outline'}
            size="sm"
            className="h-8 text-xs gap-1.5"
            data-testid="saved-views-btn"
            title="Saved views"
          >
            {activeView ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
            <span className="max-w-[120px] truncate">
              {activeView ? activeView.name : 'Saved Views'}
            </span>
            {hasViews && !activeView && (
              <Badge variant="secondary" className="h-4 px-1 text-[10px] ml-0.5">{views.length}</Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64" data-testid="saved-views-menu">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            {hasViews ? `${views.length} saved view${views.length !== 1 ? 's' : ''}` : 'No saved views yet'}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => { setSaveName(''); setSaveAsDefault(false); setSaveOpen(true); }} data-testid="save-view-trigger">
            <Plus className="w-4 h-4 mr-2" />Save current view...
          </DropdownMenuItem>
          {hasViews && <DropdownMenuSeparator />}
          {views.map(v => (
            <div key={v.id} className="flex items-center group" data-testid={`saved-view-row-${v.id}`}>
              <DropdownMenuItem
                onSelect={() => handleApply(v)}
                className="flex-1 text-xs pr-0"
                data-testid={`apply-view-${v.id}`}
              >
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  {activeView?.id === v.id ? (
                    <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                  ) : (
                    <Bookmark className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
                  )}
                  <span className="truncate">{v.name}</span>
                  {v.isDefault && (
                    <Badge variant="outline" className="h-4 px-1 text-[9px] ml-auto shrink-0 bg-amber-50 border-amber-300 text-amber-700" data-testid={`default-badge-${v.id}`}>
                      Default
                    </Badge>
                  )}
                </div>
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  className="p-1 mr-1 opacity-50 hover:opacity-100"
                  data-testid={`view-actions-${v.id}`}
                  onClick={e => e.stopPropagation()}
                >
                  <MoreVertical className="w-3.5 h-3.5" />
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="w-44">
                    <DropdownMenuItem onSelect={() => handleToggleDefault(v)} data-testid={`toggle-default-${v.id}`}>
                      <Star className={`w-4 h-4 mr-2 ${v.isDefault ? 'fill-amber-400 text-amber-500' : ''}`} />
                      {v.isDefault ? 'Unset default' : 'Set as default'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => { setRenameTarget(v); setRenameValue(v.name); }} data-testid={`rename-view-${v.id}`}>
                      <Pencil className="w-4 h-4 mr-2" />Rename
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => setDeleteTarget(v)}
                      className="text-red-600 focus:text-red-700"
                      data-testid={`delete-view-${v.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />Delete
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            </div>
          ))}
          {!hasViews && (
            <div className="px-2 py-3 text-xs text-muted-foreground text-center" data-testid="saved-views-empty">
              Save your current filters, grouping, and view mode for one-click recall later.
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Save view dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm" data-testid="save-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="w-5 h-5" />Save current view
            </DialogTitle>
            <DialogDescription>
              Captures your current view mode, grouping, and active filters. Selection and open dialogs are never saved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="save-view-name" className="text-xs">Name *</Label>
              <input
                id="save-view-name"
                type="text"
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                placeholder="e.g. NSW Open Shifts"
                maxLength={60}
                className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                data-testid="save-view-name-input"
                onKeyDown={e => e.key === 'Enter' && handleSave()}
                autoFocus
              />
              <p className="text-[11px] text-muted-foreground mt-1">Duplicate names overwrite the existing view.</p>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="save-as-default"
                checked={saveAsDefault}
                onCheckedChange={setSaveAsDefault}
                data-testid="save-as-default"
              />
              <Label htmlFor="save-as-default" className="text-xs cursor-pointer">
                Set as default (auto-load on next visit)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)} data-testid="save-view-cancel">Cancel</Button>
            <Button onClick={handleSave} disabled={!saveName.trim()} data-testid="save-view-confirm">
              <Bookmark className="w-4 h-4 mr-1" />Save view
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={o => !o && setRenameTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="rename-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />Rename view
            </DialogTitle>
            <DialogDescription>Give this saved view a new name. Duplicate names are not allowed.</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="rename-view-input" className="text-xs">New name</Label>
            <input
              id="rename-view-input"
              type="text"
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              maxLength={60}
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              data-testid="rename-view-input"
              onKeyDown={e => e.key === 'Enter' && handleRename()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button onClick={handleRename} disabled={!renameValue.trim()} data-testid="rename-view-confirm">Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-view-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />Delete saved view
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-view-confirm-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
